/**
 * SortDropdown Component
 * Allows users to sort marketplace products by different criteria
 *
 * Features:
 * - Sort by newest, price (low to high), or price (high to low)
 * - Visual icons for each sort option
 * - Responsive and mobile-friendly design
 * - Full accessibility support with aria labels
 * - Keyboard navigation support
 *
 * @component
 * @example
 * // Basic usage
 * <SortDropdown
 *   value="newest"
 *   onChange={(sortBy) => handleSort(sortBy)}
 * />
 *
 * @example
 * // With custom className
 * <SortDropdown
 *   value={currentSort}
 *   onChange={setSortBy}
 *   className="w-full md:w-auto"
 * />
 */

import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowDownUp, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Sort option values
 */
export type SortOption = 'newest' | 'price_low' | 'price_high';

/**
 * SortDropdown Props Interface
 */
export interface SortDropdownProps {
  /** Current sort value */
  value: SortOption;
  /** Callback when sort option changes */
  onChange: (value: SortOption) => void;
  /** Optional CSS class for the container */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Sort option configuration
 */
interface SortOptionConfig {
  value: SortOption;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  ariaLabel: string;
}

/**
 * Available sort options with icons and labels
 */
const SORT_OPTIONS: SortOptionConfig[] = [
  {
    value: 'newest',
    label: 'Newest',
    icon: ArrowDownUp,
    ariaLabel: 'Sort by newest products first',
  },
  {
    value: 'price_low',
    label: 'Price: Low to High',
    icon: TrendingDown,
    ariaLabel: 'Sort by price from low to high',
  },
  {
    value: 'price_high',
    label: 'Price: High to Low',
    icon: TrendingUp,
    ariaLabel: 'Sort by price from high to low',
  },
];

/**
 * Get sort option configuration by value
 */
const getSortOption = (value: SortOption): SortOptionConfig => {
  return SORT_OPTIONS.find((option) => option.value === value) || SORT_OPTIONS[0];
};

/**
 * SortDropdown Component
 * Main sorting dropdown component
 */
export const SortDropdown: React.FC<SortDropdownProps> = ({
  value,
  onChange,
  className,
  disabled = false,
}) => {
  const currentOption = getSortOption(value);
  const Icon = currentOption.icon;

  /**
   * Handle sort option change
   */
  const handleValueChange = (newValue: string) => {
    onChange(newValue as SortOption);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        'w-full md:w-auto',
        className
      )}
      role="group"
      aria-label="Sort products"
    >
      {/* Label */}
      <label
        htmlFor="sort-dropdown"
        className="text-sm font-medium text-foreground whitespace-nowrap"
      >
        Sort by:
      </label>

      {/* Select Dropdown */}
      <Select
        value={value}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          id="sort-dropdown"
          className={cn(
            'w-full md:w-[200px]',
            'transition-all duration-200',
            'hover:bg-accent',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          aria-label={currentOption.ariaLabel}
        >
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <SelectValue placeholder="Select sort option" />
          </div>
        </SelectTrigger>

        <SelectContent>
          {SORT_OPTIONS.map((option) => {
            const OptionIcon = option.icon;
            return (
              <SelectItem
                key={option.value}
                value={option.value}
                className="cursor-pointer"
                aria-label={option.ariaLabel}
              >
                <div className="flex items-center gap-2">
                  <OptionIcon
                    className="h-4 w-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="font-medium">{option.label}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

// Display name for debugging
SortDropdown.displayName = 'SortDropdown';

export default SortDropdown;
